import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import Table from "../../../core/common/dataTable/index";
import { ProbationHeader } from "./ProbationHeader";
import { matchCandidateRecord } from "./probationSearchUtils";
import { mapPvrRecord } from "./NewProbationCycle";

type Candidate = {
  empId: string;
  empName: string;
  designation?: string;
  designationId?: string;
  department?: string;
  dateOfJoining?: string;
  configuredProbationYears?: number;
  adjustedConfirmationDate?: string;
  pvrStatus?: string;
  darStatus?: string;
  darDetails?: any;
  vigilanceStatus?: string;
  committeeRemarks?: string;
  approvalRemarks?: string;
};

const ProbationCandidatesList: React.FC = () => {
  const location = useLocation();

  const [selectedCycleId, setSelectedCycleId] = useState<number | string>("");
  const [currentCycle, setCurrentCycle] = useState<any>(null);
  const [activeList, setActiveList] = useState<string>("MasterData");
  const [lastManualList, setLastManualList] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [listsData, setListsData] = useState<any>({
    masterData: [],
    eligibleList: [],
    listA_PvrIssues: [],
    listB_ProbationNotCompleted: [],
    listC_UnsuitableWrGrading: [],
    listD_DarCases: [],
    listE_VigilanceCases: []
  });

  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"wr" | "dar" | "vigilance">("wr");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  // Reset pagination on filter or cycle changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, searchTerm, activeList]);

  const handleCycleChange = (id: string | number, activeCycle: any) => {
    setSelectedCycleId(id);
    setCurrentCycle(activeCycle);
  };

  const fetchCandidates = async () => {
    if (!selectedCycleId) return;
    setLoading(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.ALL_LISTS_SUMMARY,
        { cycleId: Number(selectedCycleId) },
        true
      );
      const resData = response?.data || response;
      const innerData = resData?.data || resData;

      const deduplicate = (arr: any[]) => {
        if (!Array.isArray(arr)) return [];
        const seen = new Set();
        return arr.filter(item => {
          if (!item || !item.empId) return true;
          const duplicate = seen.has(item.empId);
          seen.add(item.empId);
          return !duplicate;
        });
      };

      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        const summary = innerData.summary || innerData;
        const eligible = deduplicate(summary.eligibleList || summary.eligible || []);
        const listA = deduplicate(summary.listA_PvrIssues || summary.listA || []);
        const listB = deduplicate(summary.listB_ProbationNotCompleted || summary.listB || []);
        const listC = deduplicate(summary.listC_UnsuitableWrGrading || summary.listC || []);
        const listD = deduplicate(summary.listD_DarCases || summary.listD || []);
        const listE = deduplicate(summary.listE_VigilanceCases || summary.listE || []);
        const returnedMaster = summary.masterData || summary.masterList || summary.master || [];
        const master = deduplicate(Array.isArray(returnedMaster) && returnedMaster.length > 0
          ? returnedMaster
          : [...listA, ...listB, ...listC, ...listD, ...listE, ...eligible]);

        setListsData({
          masterData: master,
          eligibleList: eligible,
          listA_PvrIssues: listA,
          listB_ProbationNotCompleted: listB,
          listC_UnsuitableWrGrading: listC,
          listD_DarCases: listD,
          listE_VigilanceCases: listE
        });
      } else {
        const list = deduplicate(Array.isArray(innerData) ? innerData : (innerData?.items || []));
        setListsData({
          masterData: list,
          eligibleList: list,
          listA_PvrIssues: [],
          listB_ProbationNotCompleted: [],
          listC_UnsuitableWrGrading: [],
          listD_DarCases: [],
          listE_VigilanceCases: []
        });
      }
    } catch (e) {
      console.error("Failed to fetch candidates list", e);
      setListsData({
        masterData: [],
        eligibleList: [],
        listA_PvrIssues: [],
        listB_ProbationNotCompleted: [],
        listC_UnsuitableWrGrading: [],
        listD_DarCases: [],
        listE_VigilanceCases: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Load candidates for selected cycle
  useEffect(() => {
    fetchCandidates();
  }, [selectedCycleId]);

  const getActiveArray = () => {
    switch (activeList) {
      case "MasterData":
        return listsData.masterData || [];
      case "Eligible":
        return listsData.eligibleList || [];
      case "ListA":
        return listsData.listA_PvrIssues || [];
      case "ListB":
        return listsData.listB_ProbationNotCompleted || [];
      case "ListC":
        return listsData.listC_UnsuitableWrGrading || [];
      case "ListD":
        return listsData.listD_DarCases || [];
      case "ListE":
        return listsData.listE_VigilanceCases || [];
      default:
        return [];
    }
  };

  const formatDateDMY = (str: any) => {
    if (!str || str === "N/A" || str === "null" || str === "undefined" || str === "-") return "—";
    if (typeof str !== "string") str = String(str);
    if (str.includes("T")) {
      const ymd = str.split("T")[0];
      const parts = ymd.split("-");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (str.includes("-")) {
      const parts = str.split("-");
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      if (parts.length === 3 && parts[2].length === 4) return str;
    }
    return str;
  };

  const activeCandidates = useMemo(() => {
    const rawList = getActiveArray();
    if (!Array.isArray(rawList)) return [];
    return rawList.map((c: any, idx: number) => {
      const darEval = c.dar_evaluation || c.darEvaluation || c.darDetails || c.dar_details || {};
      const wrEval = c.wr_evaluation || c.wrEvaluation || {};
      const vigEval = c.vigilance_evaluation || c.vigilanceEvaluation || {};
      const pvrEval = c.pvr_evaluation || c.pvrEvaluation || c.pvrDetails || {};

      const rawDarStatus = (
        darEval.status ?? darEval.darStatus ?? darEval.dar_status ??
        c.darStatus ?? c.dar_status ?? c.dar ?? ""
      ).toString().trim();

      const rawVigStatus = (
        vigEval.status ?? vigEval.vigilanceStatus ?? vigEval.vigilance_status ??
        c.vigilanceStatus ?? c.vigilance_status ?? c.vigilance ?? ""
      ).toString().trim();

      const rawPvrStatus = (
        pvrEval.status ?? pvrEval.pvrStatus ?? pvrEval.pvr_status ??
        c.pvrStatus ?? c.pvr_status ?? c.pvr ?? ""
      ).toString().trim();

      const mappedPvr = mapPvrRecord(c);
      const pvrRemarksVal = (
        (c.pvrRemarks && String(c.pvrRemarks).trim() !== "—" && String(c.pvrRemarks).trim() !== "")
          ? String(c.pvrRemarks).trim()
          : (c.pvr_remarks && String(c.pvr_remarks).trim() !== "—" && String(c.pvr_remarks).trim() !== "")
            ? String(c.pvr_remarks).trim()
            : (pvrEval.remarks ?? pvrEval.pvrRemarks ?? pvrEval.pvr_remarks ??
              (mappedPvr.pvrRemarks !== "—" ? mappedPvr.pvrRemarks : "—"))
      ).toString().trim();

      const vigRemarksVal = (
        vigEval.remarks ?? vigEval.vigilanceRemarks ?? vigEval.vigilance_remarks ??
        c.vigilanceRemarks ?? c.vigilance_remarks ?? "—"
      ).toString().trim();

      const rawWrStatus = (
        wrEval.status ?? wrEval.wrStatus ?? wrEval.wr_status ?? wrEval.wrFinalStatus ?? wrEval.wr_final_status ??
        c.wrStatus ?? c.wr_status ?? c.wrFinalStatus ?? c.wr_final_status ?? ""
      ).toString().trim();

      return {
        ...c,
        key: c.empId ? String(c.empId) : String(idx),
        srNo: idx + 1,
        empNo: c.empId || c.emp_id || c.empNo || c.emp_no,
        name: c.empName || c.emp_name || c.employeeName || c.name,
        designation: c.designation || c.designationId || "N/A",
        department: c.department || "N/A",
        doj: c.dateOfJoining || c.date_of_joining || "N/A",
        probationYears: c.configuredProbationYears || c.probationYears || 1,
        confirmationDate: c.confirmationDate || c.confirmation_date || c.adjustedConfirmationDate || c.adjusted_confirmation_date || c.probationDueDate || c.probation_due_date || c.confirmationDueOn || c.confirmation_due_on || "N/A",
        adjustedConfirmationDate: c.adjustedConfirmationDate || c.confirmation_date || "N/A",
        pvrStatus: rawPvrStatus || "N/A",
        pvrRemarks: pvrRemarksVal,
        darStatus: rawDarStatus || "CLEAR",
        dar_evaluation: {
          darCleared: darEval.darCleared ?? darEval.dar_cleared ?? c.darCleared ?? (rawDarStatus.toUpperCase() === "DAR CLEAR" || rawDarStatus.toUpperCase() === "CLEAR"),
          status: rawDarStatus || "DAR CLEAR",
          darStatus: rawDarStatus || "DAR CLEAR",
          caseNo: darEval.caseNo ?? darEval.case_no ?? c.caseNo ?? c.case_no ?? c.darDetails?.caseNo ?? c.dar_details?.caseNo ?? null,
          chargeSheetIssuedDate: darEval.chargeSheetIssuedDate ?? darEval.charge_sheet_issued_date ?? c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.darDetails?.chargeSheetIssuedDate ?? c.dar_details?.chargeSheetIssuedDate ?? null,
          penaltyDescription: darEval.penaltyDescription ?? darEval.penalty_description ?? c.penaltyDescription ?? c.penalty_description ?? c.darDetails?.penaltyDescription ?? null,
          dateOfAction: darEval.dateOfAction ?? darEval.date_of_action ?? c.dateOfAction ?? c.date_of_action ?? c.darDetails?.dateOfAction ?? null,
          penaltyStartDate: darEval.penaltyStartDate ?? darEval.penalty_start_date ?? c.penaltyStartDate ?? c.penalty_start_date ?? c.darDetails?.penaltyStartDate ?? null,
          penaltyEndDate: darEval.penaltyEndDate ?? darEval.penalty_end_date ?? c.penaltyEndDate ?? c.penalty_end_date ?? c.darDetails?.penaltyEndDate ?? null,
        },
        vigilanceStatus: rawVigStatus || "CLEAR",
        vigilanceRemarks: vigRemarksVal,
        vigilance_evaluation: {
          status: rawVigStatus || "CLEAR",
          vigilanceCleared: vigEval.vigilanceCleared ?? vigEval.vigilance_cleared ?? c.vigilanceCleared ?? c.vigilance_cleared ?? true,
          vigilanceRemarks: vigRemarksVal,
          criminalCase: vigEval.criminalCase ?? vigEval.criminal_case ?? c.criminalCase ?? c.criminal_case ?? "no",
          criminalStatus: vigEval.criminalStatus ?? vigEval.criminal_status ?? c.criminalStatus ?? c.criminal_status ?? "—",
          criminalCleared: vigEval.criminalCleared ?? vigEval.criminal_cleared ?? c.criminalCleared ?? c.criminal_cleared ?? true,
          vigilanceType: vigEval.vigilanceType ?? vigEval.vigilance_type ?? c.vigilanceType ?? c.vigilance_type ?? "—"
        },
        wrStatus: rawWrStatus || "SUITABLE",
        wr_evaluation: wrEval,
        remarks: c.committeeRemarks || c.approvalRemarks || "-"
      };
    });
  }, [listsData, activeList]);

  useEffect(() => {
    if (!searchTerm || !searchTerm.trim()) {
      if (lastManualList) {
        setActiveList(lastManualList);
        setLastManualList(null);
      }
      return;
    }

    const currentArray = getActiveArray();
    const hasCurrentMatch = (currentArray || []).some((c: any) => matchCandidateRecord(c, searchTerm));

    if (!hasCurrentMatch) {
      const listKeys: Array<{ key: string; data: any[] }> = [
        { key: "ListA", data: listsData.listA_PvrIssues },
        { key: "ListB", data: listsData.listB_ProbationNotCompleted },
        { key: "ListC", data: listsData.listC_UnsuitableWrGrading },
        { key: "ListD", data: listsData.listD_DarCases },
        { key: "ListE", data: listsData.listE_VigilanceCases },
        { key: "Eligible", data: listsData.eligibleList },
        { key: "MasterData", data: listsData.masterData }
      ];

      for (const item of listKeys) {
        if (Array.isArray(item.data) && item.data.some((c: any) => matchCandidateRecord(c, searchTerm))) {
          setLastManualList((prev) => prev || activeList);
          setActiveList(item.key);
          break;
        }
      }
    }
  }, [searchTerm, listsData]);

  const filteredCandidates = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) return activeCandidates;
    return activeCandidates.filter((c: any) => matchCandidateRecord(c, searchTerm));
  }, [activeCandidates, searchTerm]);

  const getDarBadgeDetails = (e: any) => {
    const rawStatus = (e.darStatus ?? e.dar_status ?? e.dar_evaluation?.status ?? e.dar_evaluation?.darStatus ?? e.dar ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "DAR CLEAR",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
      return {
        text: "ChargeSheet Issued",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
      return {
        text: "Minor Penalty",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
      return {
        text: "Major Penalty",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getVigilanceBadgeDetails = (e: any) => {
    const rawStatus = (e.vigilanceStatus ?? e.vigilance_status ?? e.vigilance ?? e.vigilance_evaluation?.status ?? e.vigilanceType ?? e.vigilance_type ?? e.vigilanceStatusText ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "no vigilance case" || statusLower === "no_vigilance_case" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "No Vigilance Case",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "vigilance initiated" || statusLower === "vigilance_initiated") {
      return {
        text: "Vigilance Initiated",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "penalty awarded" || statusLower === "penalty_awarded") {
      return {
        text: "Penalty Awarded",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getPvrBadgeDetails = (e: any) => {
    const { pvrStatus } = mapPvrRecord(e);
    if (pvrStatus === "NO ADVERSE") {
      return {
        text: "NO ADVERSE",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (pvrStatus === "ISSUE IN PVR") {
      return {
        text: "ISSUE IN PVR",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: "In Process",
      badgeClass: "bg-amber text-dark",
      badgeStyle: { backgroundColor: "#fffbeb", color: "#d97706", border: "1.5px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getWrBadgeDetails = (e: any) => {
    const rawStatus = (e.wrStatus ?? e.wr_status ?? e.wrFinalStatus ?? e.wr_final_status ?? e.wr_evaluation?.status ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "suitable" || statusLower === "clear" || statusLower === "passed" || statusLower === "wr clear" || statusLower === "satisfactory") {
      return {
        text: "Suitable",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "not suitable" || statusLower === "unsuitable" || statusLower === "failed" || statusLower === "wr not suitable") {
      return {
        text: "Not Suitable",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  // Define Table Columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 65
      },
      {
        title: "Emp No",
        dataIndex: "empNo",
        render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
      },
      {
        title: "Name",
        dataIndex: "name",
        render: (val: string) => <div className="fw-bold text-dark">{val}</div>
      },
    ];

    return [
      ...baseColumns,
      {
        title: activeList === "MasterData" ? "Service (Years)" : "Date of Joining",
        dataIndex: activeList === "MasterData" ? "probationYears" : "doj",
        render: (val: any) => activeList === "MasterData" ? (val ?? "N/A") : formatDateDMY(val)
      },
      {
        title: "PVR Status",
        dataIndex: "pvrStatus",
        render: (_: any, record: any) => {
          const b = getPvrBadgeDetails(record);
          return <span className={`badge ${b.badgeClass}`} style={b.badgeStyle}>{b.text}</span>;
        }
      },
      {
        title: "PVR Remarks",
        dataIndex: "pvrRemarks",
        render: (val: string, record: any) => {
          const direct = (val && String(val).trim() !== "—" && String(val).trim() !== "" && String(val).trim() !== "null")
            ? String(val).trim()
            : (record?.pvrRemarks && String(record.pvrRemarks).trim() !== "—" && String(record.pvrRemarks).trim() !== "" && String(record.pvrRemarks).trim() !== "null")
              ? String(record.pvrRemarks).trim()
              : (record?.pvr_remarks && String(record.pvr_remarks).trim() !== "—" && String(record.pvr_remarks).trim() !== "" && String(record.pvr_remarks).trim() !== "null")
                ? String(record.pvr_remarks).trim()
                : mapPvrRecord(record).pvrRemarks;
          return direct || "—";
        }
      },
      {
        title: "DAR",
        dataIndex: "darStatus",
        align: "center" as const,
        render: (_: any, record: any) => {
          const darBadge = getDarBadgeDetails(record);
          return <span className={`badge ${darBadge.badgeClass}`} style={darBadge.badgeStyle}>{darBadge.text}</span>;
        }
      },
      {
        title: "WR Details",
        dataIndex: "wrStatus",
        align: "center" as const,
        render: (_: any, record: any) => {
          const wrBadge = getWrBadgeDetails(record);
          return <span className={`badge ${wrBadge.badgeClass}`} style={wrBadge.badgeStyle}>{wrBadge.text}</span>;
        }
      },
      {
        title: "Vigilance",
        dataIndex: "vigilanceStatus",
        align: "center" as const,
        render: (_: any, record: any) => {
          const vigBadge = getVigilanceBadgeDetails(record);
          return <span className={`badge ${vigBadge.badgeClass}`} style={vigBadge.badgeStyle}>{vigBadge.text}</span>;
        }
      },
      {
        title: "Vigilance Remarks",
        dataIndex: "vigilanceRemarks",
        render: (val: string) => val || "—"
      },
      {
        title: "Effective Date",
        dataIndex: "adjustedConfirmationDate",
        render: (val: string) => formatDateDMY(val)
      },
      {
        title: "Evaluation Details",
        dataIndex: "evaluationDetails",
        render: (_: any, record: any) => (
          <button
            type="button"
            className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
            style={{ fontSize: "11px", borderRadius: "5px", whiteSpace: "nowrap" }}
            onClick={() => {
              setSelectedEvaluationCandidate(record);
              setActiveModalTab("wr");
            }}
          >
            <i className="ti ti-eye fs-6"></i> View Details
          </button>
        )
      }
    ];
  }, [pagination, activeList]);

  const probationOverview = [
    {
      key: "MasterData",
      title: "Master Data",
      subtitle: "All Candidates",
      count: (listsData.masterData || []).length,
      color: "#1e40af",
      bgColor: "#eff6ff",
      icon: "database"
    },
    {
      key: "ListA",
      title: "List A",
      subtitle: "PVR Not Suitable",
      count: (listsData.listA_PvrIssues || []).length,
      color: "#ef4444",
      bgColor: "#fef2f2",
      icon: "clock"
    },
    {
      key: "ListB",
      title: "List B",
      subtitle: "Service Shortfall",
      count: (listsData.listB_ProbationNotCompleted || []).length,
      color: "#f59e0b",
      bgColor: "#fefbeb",
      icon: "scale"
    },
    {
      key: "ListC",
      title: "List C",
      subtitle: "WR Not Suitable",
      count: (listsData.listC_UnsuitableWrGrading || []).length,
      color: "#f97316",
      bgColor: "#fff7ed",
      icon: "report-off"
    },
    {
      key: "ListD",
      title: "List D",
      subtitle: "DAR Not Cleared",
      count: (listsData.listD_DarCases || []).length,
      color: "#dc2626",
      bgColor: "#fef2f2",
      icon: "gavel"
    },
    {
      key: "ListE",
      title: "List E",
      subtitle: "Vigilance Not Cleared",
      count: (listsData.listE_VigilanceCases || []).length,
      color: "#7c3aed",
      bgColor: "#f5f3ff",
      icon: "shield-alert"
    },
    {
      key: "Eligible",
      title: "Eligible",
      subtitle: "Clear Candidates",
      count: (listsData.eligibleList || []).length,
      color: "#22c55e",
      bgColor: "#f0fdf4",
      icon: "circle-check"
    },
  ];

  const tabs = [
    { key: "MasterData", label: `Master Data (${(listsData.masterData || []).length})`, color: "#1e40af" },
    { key: "ListA", label: `List A (${(listsData.listA_PvrIssues || []).length})`, color: "#ef4444" },
    { key: "ListB", label: `List B (${(listsData.listB_ProbationNotCompleted || []).length})`, color: "#f59e0b" },
    { key: "ListC", label: `List C (${(listsData.listC_UnsuitableWrGrading || []).length})`, color: "#f97316" },
    { key: "ListD", label: `List D (${(listsData.listD_DarCases || []).length})`, color: "#dc2626" },
    { key: "ListE", label: `List E (${(listsData.listE_VigilanceCases || []).length})`, color: "#7c3aed" },
    { key: "Eligible", label: `Eligible (${(listsData.eligibleList || []).length})`, color: "#22c55e" },
  ];

  const getResumeStep = (status: string) => {
    const s = String(status || "").trim().toUpperCase();
    switch (s) {
      case "INITIATED":
        return 2;
      case "PVR_CLEARED":
      case "PVR_CLEAR":
      case "PVR_STATUS_SET":
      case "PVR_CALCULATED":
      case "PVR_DONE":
        return 3;
      case "ELIGIBILITY_CALCULATED":
      case "CALCULATED":
      case "CANDIDATES_CALCULATED":
        return 4;
      case "WR_GRADING":
      case "WR_CLEARED":
      case "WR_CLEAR":
      case "WR_SUITABLE":
      case "WR_CALCULATED":
      case "WR_STATUS_SET":
        return 5;
      case "DAR_CLEARED":
      case "DAR_CLEAR":
      case "DAR_STATUS_SET":
      case "DAR_CALCULATED":
      case "DAR_SUITABLE":
        return 6;
      case "VIGILANCE_CLEARED":
      case "VIGILANCE_CLEAR":
      case "VIGILANCE_STATUS_SET":
      case "VIGILANCE_CALCULATED":
      case "VIGILANCE_UPDATED":
      case "ELIGIBLE_GENERATED":
        return 7;
      default:
        return 2;
    }
  };

  const renderActionButton = (cycle: any) => {
    const s = String(cycle?.status || "").trim().toUpperCase();
    if (s === "COMPETENT_AUTHORITY_APPROVAL") {
      return (
        <Link
          to={`${all_routes.probation.approvalList}?cycleId=${cycle.id}`}
          className="btn btn-success btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "30px", fontSize: "12px" }}
        >
          <i className="ti ti-check" style={{ fontSize: "14px" }}></i> Competent Authority Review
        </Link>
      );
    }
    if (s === "SCREENING_COMMITTEE" || s === "REVIEWED" || s === "COMMITTEE_REVIEW") {
      return (
        <Link
          to={`${all_routes.probation.committeeReview}?cycleId=${cycle.id}`}
          className="btn btn-primary btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "30px", fontSize: "12px" }}
        >
          <i className="ti ti-file-search" style={{ fontSize: "14px" }}></i> Committee Review
        </Link>
      );
    }
    if (s === "APPROVED" || s === "OFFICE_ORDER_GENERATED" || s === "COMPLETED") {
      return (
        <Link
          to={`${all_routes.probation.officeOrders}?cycleId=${cycle.id}`}
          className="btn btn-info btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "30px", fontSize: "12px" }}
        >
          <i className="ti ti-file-text" style={{ fontSize: "14px" }}></i> Office Orders
        </Link>
      );
    }
    return (
      <Link
        to={`${all_routes.probation.newCycle}?cycleId=${cycle.id}&step=${getResumeStep(s)}`}
        className="btn btn-warning btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
        style={{ borderRadius: "6px", height: "30px", fontSize: "12px" }}
      >
        <i className="ti ti-player-play" style={{ fontSize: "14px" }}></i> Resume Setup
      </Link>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <ProbationHeader
          title="Probation Lists (A-E)"
          subtitle="Screening lists for eligibility and exceptions"
          selectedCycleId={selectedCycleId}
          onCycleChange={handleCycleChange}
          extra={
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Link
                to={all_routes.probation.newCycle}
                className="btn btn-danger btn-sm text-nowrap d-inline-flex align-items-center justify-content-center"
                style={{ height: 36, whiteSpace: "nowrap" }}
              >
                New Probation Cycle
              </Link>
              <Link to={all_routes.probation.cyclesDashboard} className="btn btn-light btn-sm border text-nowrap d-inline-flex align-items-center justify-content-center" style={{ height: 36 }}>
                Back
              </Link>
            </div>
          }
        />

        {currentCycle && (
          <div className="card mb-4 border shadow-none" style={{ borderRadius: "10px", overflow: "hidden" }}>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-nowrap mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Active Cycle</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Financial Year</th>
                      <th style={{ display: "none" }}>Quarter</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Pay Scales</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Current Status</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }} className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "12px 16px" }} className="fw-bold text-dark">
                        {(() => {
                          let titleStr = currentCycle.title || `Cycle #${currentCycle.id}`;
                          if (titleStr.includes("[")) {
                            titleStr = titleStr.substring(0, titleStr.indexOf("[")).trim();
                          }
                          return titleStr;
                        })()}
                      </td>
                      <td style={{ padding: "12px 16px" }} className="text-secondary">{currentCycle.financialYear}</td>
                      <td style={{ padding: "12px 16px" }}>
                        {(() => {
                          let scalesList: string[] = [];
                          try {
                            const parsed = typeof currentCycle.grade === "string" ? JSON.parse(currentCycle.grade) : currentCycle.grade;
                            if (Array.isArray(parsed)) {
                              scalesList = parsed.map((s: any) => String(s).trim());
                            } else if (typeof parsed === "string") {
                              scalesList = parsed.split(",").map((s: string) => s.trim());
                            }
                          } catch {
                            if (typeof currentCycle.grade === "string") {
                              scalesList = currentCycle.grade.split(",").map((s: string) => s.trim());
                            }
                          }

                          if (scalesList.length === 0 && currentCycle.title && currentCycle.title.includes("[")) {
                            const match = currentCycle.title.match(/\[(.*?)\]/);
                            if (match && match[1]) {
                              scalesList = match[1].split(",").map((s: string) => s.trim());
                            }
                          }

                          const cleanScales = scalesList.filter(Boolean);
                          if (cleanScales.length === 0) return <span className="text-muted">N/A</span>;

                          return (
                            <div className="d-flex flex-wrap gap-1 align-items-center" style={{ maxWidth: "550px" }}>
                              {cleanScales.map((scale, idx) => (
                                <span
                                  key={idx}
                                  className="badge bg-primary-transparent text-primary fw-medium"
                                  style={{ fontSize: "11px", padding: "4px 8px", borderRadius: "6px", border: "1px solid rgba(59, 130, 246, 0.2)" }}
                                >
                                  {scale}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span className="badge bg-soft-success text-success px-2.5 py-1 border border-success-subtle fw-semibold" style={{ fontSize: "11px" }}>
                          {currentCycle.status}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px" }} className="text-end">
                        {renderActionButton(currentCycle)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "10px"
          }}
        >
          {probationOverview.map((sec) => {
            const isActive = activeList === sec.key;
            return (
              <div
                key={sec.key}
                onClick={() => {
                  setActiveList(sec.key);
                  setLastManualList(sec.key);
                }}
                className="p-2 px-3 rounded-3 position-relative overflow-hidden"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isActive
                    ? "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                    : "0 1px 3px rgba(0,0,0,0.02)",
                  transform: isActive ? "translateY(-4px)" : "translateY(0)"
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 10px rgba(0, 0, 0, 0.04)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.02)";
                  } else {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)";
                  }
                }}
              >
                {/* Left accent strip */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    background: sec.color
                  }}
                />

                {/* Header row with Title and Icon */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span
                    className="fw-bold text-truncate me-1"
                    style={{
                      fontSize: "11px",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: isActive ? "#1f2937" : "#6b7280"
                    }}
                  >
                    {sec.title}
                  </span>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "22px",
                      height: "22px",
                      background: sec.bgColor,
                      color: sec.color
                    }}
                  >
                    <i className={`ti ti-${sec.icon}`} style={{ fontSize: "11px" }}></i>
                  </div>
                </div>

                {/* Subtitle */}
                <div className="text-secondary mb-1 text-truncate" style={{ fontSize: "10px", fontWeight: 500 }}>
                  {sec.subtitle}
                </div>

                {/* Metric Value count */}
                <div
                  className="h4 mb-0"
                  style={{
                    fontWeight: 700,
                    color: sec.color,
                    fontSize: "18px"
                  }}
                >
                  {sec.count}
                </div>
              </div>
            );
          })}
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="mb-3 fw-bold text-dark">Employee Lists</h5>

            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-1.5" role="tablist" aria-label="lists">
                {tabs.map(t => {
                  const isActive = activeList === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setActiveList(t.key);
                        setLastManualList(t.key);
                      }}
                      className="btn transition-all text-nowrap"
                      style={{
                        backgroundColor: isActive ? t.color : "#f8f9fa",
                        color: isActive ? "#ffffff" : "#495057",
                        borderColor: isActive ? t.color : "#dee2e6",
                        fontWeight: isActive ? 600 : 500,
                        fontSize: "12px",
                        padding: "6px 14px",
                        borderRadius: "6px"
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={filteredCandidates}
              paginationConfig={pagination}
              Selection={false}
              loading={loading}
              scroll={{ x: "max-content" }}
              handleSearch={setSearchTerm}
              searchValue={searchTerm}
              searchPlaceholder="Search candidates"
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
            />
          </div>
        </div>

        {/* Candidate Evaluation Details Modal */}
        {selectedEvaluationCandidate && (() => {
          const c = selectedEvaluationCandidate;
          const empIdVal = String(c.empNo || c.empId || c.emp_no || "—");
          const empNameVal = String(c.name || c.employeeName || c.empName || c.emp_name || "—");

          // Parse WR scores
          let wrScores: Record<string, any> = {};
          const rawWrScores = c.wr_evaluation?.scores ?? c.wr_scores ?? c.wrScores ?? c.year_scores ?? c.yearScores;
          if (typeof rawWrScores === "string") {
            try {
              wrScores = JSON.parse(rawWrScores);
            } catch (e) {
              wrScores = {};
            }
          } else if (rawWrScores && typeof rawWrScores === "object") {
            wrScores = rawWrScores;
          } else {
            if (c.wrYear1 || c.wr_year_1) wrScores["Year 1"] = c.wrYear1 || c.wr_year_1;
            if (c.wrYear2 || c.wr_year_2) wrScores["Year 2"] = c.wrYear2 || c.wr_year_2;
          }

          // D&AR status details
          const dar = getDarBadgeDetails(c);
          const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? "").toString().trim();
          const darStatusLower = rawDarStatus.toLowerCase();
          const isChargeSheet = darStatusLower === "chargesheet issued" || darStatusLower === "chargesheet_issued" || darStatusLower === "chargesheetissued";
          const isMinor = darStatusLower === "minor penalty" || darStatusLower === "minor_penalty" || darStatusLower === "minorpenalty";
          const isMajor = darStatusLower === "major penalty" || darStatusLower === "major_penalty" || darStatusLower === "majorpenalty";
          const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

          const darObj = c.dar_evaluation || c.darEvaluation || c.darDetails || c.dar_details || {};
          const darCaseNo = c.caseNo ?? c.case_no ?? darObj.caseNo ?? darObj.case_no ?? c.darDetails?.caseNo ?? c.dar_details?.caseNo ?? "—";
          const darChargeSheetDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? darObj.chargeSheetIssuedDate ?? darObj.charge_sheet_issued_date ?? c.darDetails?.chargeSheetIssuedDate ?? c.dar_details?.chargeSheetIssuedDate);
          const darPenaltyDesc = c.penaltyDescription ?? c.penalty_description ?? darObj.penaltyDescription ?? darObj.penalty_description ?? c.darDetails?.penaltyDescription ?? "—";
          const darDateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? darObj.dateOfAction ?? darObj.date_of_action ?? c.darDetails?.dateOfAction);
          const darPenaltyStart = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? darObj.penaltyStartDate ?? darObj.penalty_start_date ?? c.darDetails?.penaltyStartDate);
          const darPenaltyEnd = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? darObj.penaltyEndDate ?? darObj.penalty_end_date ?? c.darDetails?.penaltyEndDate);

          // Vigilance & PVR details
          const pvrBadge = getPvrBadgeDetails(c);
          const pvrRemarks = (
            (c.pvrRemarks && String(c.pvrRemarks).trim() !== "—" && String(c.pvrRemarks).trim() !== "" && String(c.pvrRemarks).trim() !== "null")
              ? String(c.pvrRemarks).trim()
              : (c.pvr_remarks && String(c.pvr_remarks).trim() !== "—" && String(c.pvr_remarks).trim() !== "" && String(c.pvr_remarks).trim() !== "null")
                ? String(c.pvr_remarks).trim()
                : (c.pvr_evaluation?.remarks ?? mapPvrRecord(c).pvrRemarks ?? "—")
          );

          const vigBadge = getVigilanceBadgeDetails(c);
          const vigStatus = c.vigilance_evaluation?.status ?? c.vigilanceStatus ?? c.vigilance_status ?? "—";
          const vigClearedStr = c.vigilance_evaluation?.vigilanceCleared !== undefined
            ? (c.vigilance_evaluation.vigilanceCleared ? "Cleared" : "Not Cleared")
            : (c.vigilanceCleared !== undefined ? (c.vigilanceCleared ? "Cleared" : "Not Cleared") : "—");

          const crimStatus = c.criminal_evaluation?.status ?? c.criminalStatus ?? c.criminal_status ?? "—";
          const crimClearedStr = c.criminal_evaluation?.criminalCleared !== undefined
            ? (c.criminal_evaluation.criminalCleared ? "Cleared" : "Not Cleared")
            : (c.criminalCleared !== undefined ? (c.criminalCleared ? "Cleared" : "Not Cleared") : "—");

          const vigType = c.vigilanceType ?? c.vigilance_type ?? "—";
          const crimCaseVal = c.criminalCase !== undefined
            ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : (c.criminalCase === "no" || c.criminalCase === "No" ? "No" : String(c.criminalCase)))
            : (c.criminal_case !== undefined ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : (c.criminal_case === "no" || c.criminal_case === "No" ? "No" : String(c.criminal_case))) : "—");
          const vigRemarks = c.vigilanceRemarks ?? c.vigilance_remarks ?? c.vigilance_evaluation?.vigilanceRemarks ?? "—";

          return (
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
              onClick={() => setSelectedEvaluationCandidate(null)}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={{ maxWidth: "650px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
                  {/* Header */}
                  <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                    <div>
                      <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                        <i className="ti ti-file-analytics text-primary fs-5"></i>
                        Candidate Evaluation Details
                      </h6>
                      <div className="text-muted small mt-1">
                        Employee: <span className="fw-bold text-dark">{empNameVal}</span> ({empIdVal})
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedEvaluationCandidate(null)}
                      aria-label="Close"
                    ></button>
                  </div>

                  {/* Navigation Tabs */}
                  <div className="px-4 pt-3 bg-white border-bottom">
                    <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-3 mb-0">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "wr" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("wr")}
                        >
                          <i className="ti ti-file-text me-1.5"></i> WR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("dar")}
                        >
                          <i className="ti ti-scale me-1.5"></i> D&AR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("vigilance")}
                        >
                          <i className="ti ti-shield-check me-1.5"></i> Vigilance & PVR Details
                        </button>
                      </li>
                    </ul>
                  </div>

                  {/* Body Content */}
                  <div className="modal-body p-4" style={{ minHeight: "260px" }}>
                    {/* Tab 1: WR Details */}
                    {activeModalTab === "wr" && (
                      <div>
                        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                          Work Report (WR) Evaluation & Scores
                        </h6>
                        {Object.keys(wrScores).length === 0 ? (
                          <div className="alert alert-light border text-muted small p-3 rounded">
                            No individual year WR scores provided.
                          </div>
                        ) : (
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "14px" }}>
                              <thead style={{ backgroundColor: "#f1f5f9" }}>
                                <tr>
                                  <th className="py-2.5 px-3" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>Year</th>
                                  <th className="py-2.5 px-3 text-end" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>WR Score / Grading</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(wrScores).map(([yr, score]) => (
                                  <tr key={yr}>
                                    <td className="py-2.5 px-3 fw-semibold text-dark" style={{ fontSize: "14px" }}>{yr}</td>
                                    <td className="py-2.5 px-3 text-end fw-bold text-primary" style={{ fontSize: "16px" }}>
                                      {String(score)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="row g-3 mt-1">
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Final Status</div>
                              <div className="fw-bold mt-1" style={{ fontSize: "18px", lineHeight: "1.2", color: c.wr_evaluation?.status === "FAILED" ? "#dc2626" : "#16a34a" }}>
                                {c.wr_evaluation?.status ?? c.wrFinalStatus ?? c.wrStatus ?? "SUITABLE"}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Required Benchmark</div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: "18px", lineHeight: "1.2" }}>
                                {c.wr_evaluation?.requiredBenchmark ?? "Satisfactory"}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Result</div>
                              <div>
                                <span className={`badge ${c.wr_evaluation?.status === "FAILED" ? "bg-danger" : "bg-success"}`} style={{ fontSize: "12px", padding: "5px 12px", fontWeight: 700 }}>
                                  {c.wr_evaluation?.status ?? "PASSED"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Suitability</div>
                              <div className="fw-bold" style={{ fontSize: "15px", color: (c.wr_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {c.wr_evaluation?.isSuitable !== undefined
                                  ? (c.wr_evaluation.isSuitable ? "Suitable" : "Not Suitable")
                                  : "Suitable"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: D&AR Details */}
                    {activeModalTab === "dar" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">D&AR Status</span>
                          <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
                            {dar.text}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Case No.</span>
                          <span className="fw-semibold text-dark">{darCaseNo}</span>
                        </div>

                        {(isChargeSheet || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                            <span className="fw-semibold text-dark">{darChargeSheetDate}</span>
                          </div>
                        )}

                        {(isMinor || isMajor || isClearOrNone) && (
                          <div className="pb-2 border-bottom">
                            <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                            <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                              {darPenaltyDesc}
                            </div>
                          </div>
                        )}

                        {(isMinor || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Date of Action</span>
                            <span className="fw-semibold text-dark">{darDateOfAction}</span>
                          </div>
                        )}

                        {(isMajor || isClearOrNone) && (
                          <>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty Start Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyStart}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty End Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyEnd}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Tab 3: Vigilance & PVR Details */}
                    {activeModalTab === "vigilance" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="p-2.5 rounded bg-light border-start border-4 border-primary mb-1">
                          <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "13px" }}>PVR Details</h6>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">PVR Status</span>
                          <span className={`badge ${pvrBadge.badgeClass}`} style={pvrBadge.badgeStyle}>
                            {pvrBadge.text}
                          </span>
                        </div>

                        <div className="pb-2 border-bottom">
                          <span className="text-secondary font-medium d-block mb-1">PVR Remarks</span>
                          <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                            {pvrRemarks}
                          </div>
                        </div>

                        <div className="p-2.5 rounded bg-light border-start border-4 border-info my-1">
                          <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "13px" }}>Vigilance Details</h6>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Status</span>
                          <span className={`badge ${vigBadge.badgeClass}`} style={vigBadge.badgeStyle}>
                            {vigBadge.text}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Cleared</span>
                          <span className={`badge ${vigClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {vigClearedStr}
                          </span>
                        </div>



                        <div className="pb-2">
                          <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                          <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                            {vigRemarks}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="modal-footer py-2.5 px-4 bg-light border-top d-flex justify-content-end" style={{ borderRadius: "0 0 14px 14px" }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm px-4 fw-semibold"
                      style={{ borderRadius: "6px" }}
                      onClick={() => setSelectedEvaluationCandidate(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ProbationCandidatesList;
